import Protomux from 'protomux'
import c from 'compact-encoding'
import b4a from 'b4a'
import z32 from 'z32'
import {
  Identity, domainHash, seal, sealOpen, toBoxPublicKey, randomBytes, hash
} from '@pine/crypto'
import { Emitter } from './emitter.js'

/**
 * Adding a second device to an account.
 *
 * An account is a seed, and a device that holds it is the account. So linking
 * a new phone or a laptop means moving that seed, and the whole design here is
 * about the two ways that can go wrong: somebody else receiving it, and
 * somebody else sending a different one.
 *
 * The device being added generates a throwaway keypair and shows it as a code.
 * Whoever scans the code seals the seed to that key, so only the device that
 * generated it can open the message — the relay, the DHT and anyone watching
 * the topic see a sealed box and nothing else. The code also carries a secret
 * that never becomes part of the topic, and the sender proves it knows that
 * secret; without it, anyone able to find the topic could answer first with a
 * seed of their own choosing and quietly own the new device.
 *
 * What this cannot defend against is the person holding the phone scanning a
 * code somebody else put in front of them. No pairing scheme can. The
 * approving device asks before it sends, which is the only honest mitigation.
 */

const PROTOCOL = 'pine/link/1'
const PREFIX = 'pine-link/1:'
const frame = { preencode: c.buffer.preencode, encode: c.buffer.encode, decode: c.buffer.decode }

/** See pairing.js — an onmessage handler must never return a pending promise. */
function detach (fn) {
  return (...args) => { Promise.resolve().then(() => fn(...args)).catch(() => {}) }
}

/**
 * The topic is derived from the throwaway public key alone.
 *
 * Deliberately not from the secret: the secret has to stay out of anything an
 * observer can arrive at, which is exactly what makes it worth proving.
 */
export function linkTopic (publicKey) {
  return domainHash('pine/link/topic/v1', publicKey)
}

function linkProof (secret, body, at) {
  return hash(domainHash('pine/link/proof/v1', body, String(at)), { key: secret })
}

export function parseLinkCode (input) {
  const text = String(input || '').trim()
  const body = text.startsWith(PREFIX) ? text.slice(PREFIX.length) : null
  if (!body) return null

  const [publicKey, secret] = body.split(':')
  if (!publicKey || !secret) return null
  try {
    const key = z32.decode(publicKey)
    const salt = z32.decode(secret)
    if (key.byteLength !== 32 || salt.byteLength !== 32) return null
    return { publicKey: key, secret: salt }
  } catch {
    return null
  }
}

const payloadEncoding = {
  preencode (state, m) {
    c.string.preencode(state, m.body)
    c.uint.preencode(state, m.at)
    c.fixed32.preencode(state, m.proof)
  },
  encode (state, m) {
    c.string.encode(state, m.body)
    c.uint.encode(state, m.at)
    c.fixed32.encode(state, m.proof)
  },
  decode (state) {
    return {
      body: c.string.decode(state),
      at: c.uint.decode(state),
      proof: c.fixed32.decode(state)
    }
  }
}

/**
 * A one-shot sealed drop box addressed to *this device*.
 *
 * The account inbox is addressed to an account, and once an account has more
 * than one device that is the wrong address: a reply meant for the phone that
 * asked can just as easily be answered by the laptop, which then quietly
 * swallows it. This is a throwaway keypair, a topic derived from it, and a
 * single sealed message — the reply comes back to the machine that asked and
 * to nothing else.
 *
 * Used twice: to hand an account to a new device, and to hand a room invite
 * back to a device restoring a chat.
 */
export class SealedBox extends Emitter {
  constructor (transport) {
    super()
    this.transport = transport
    this.ephemeral = Identity.generate({ name: '' })
    this.secret = randomBytes(32)
    this.topic = linkTopic(this.ephemeral.publicKey)

    this.code = PREFIX +
      z32.encode(this.ephemeral.publicKey) + ':' + z32.encode(this.secret)

    this.channels = new Set()
    this.muxes = new Set()
    this._detach = null
    this._settle = null
    this.closed = false
  }

  /** @returns a promise for the sealed body as a string, or null if cancelled. */
  start ({ timeout = 5 * 60 * 1000 } = {}) {
    if (this._promise) return this._promise

    this._promise = new Promise((resolve) => {
      this._settle = resolve
      this._timer = setTimeout(() => this.cancel(), timeout)
      if (this._timer.unref) this._timer.unref()
    })

    this._detach = this.transport.onConnection((conn) => this._attach(conn))
    for (const conn of this.transport.connections) this._attach(conn)
    this.transport.join(this.topic, { server: true, client: false })
    this.transport.announced(this.topic).then(() => this.emit('listening')).catch(() => {})

    return this._promise
  }

  cancel () {
    this._finish(null)
  }

  _finish (result) {
    if (this.closed) return
    this.closed = true
    if (this._timer) clearTimeout(this._timer)
    if (this._detach) this._detach()

    for (const mux of this.muxes) {
      try { mux.unpair({ protocol: PROTOCOL, id: this.topic }) } catch {}
    }
    this.muxes.clear()
    for (const ch of this.channels) {
      try { ch.close() } catch {}
    }
    this.channels.clear()
    this.transport.leave(this.topic).catch(() => {})

    if (this._settle) this._settle(result)
  }

  _attach (stream) {
    const mux = Protomux.from(stream)
    this.muxes.add(mux)
    mux.pair({ protocol: PROTOCOL, id: this.topic }, () => this._open(mux))
    this._open(mux)
  }

  _open (mux) {
    const channel = mux.createChannel({
      protocol: PROTOCOL,
      id: this.topic,
      onclose: () => this.channels.delete(channel)
    })
    if (!channel) return

    channel.addMessage({
      encoding: frame,
      onmessage: detach((buf) => this._onPayload(buf))
    })
    channel.open()
    this.channels.add(channel)
  }

  _onPayload (buf) {
    if (this.closed) return

    const raw = sealOpen(buf, this.ephemeral.boxPublicKey, this.ephemeral.boxSecretKey)
    if (!raw) return

    let payload
    try {
      payload = payloadEncoding.decode({ start: 0, end: raw.byteLength, buffer: raw })
    } catch {
      return
    }

    // Whoever sent this has to have seen the code, not merely found the topic.
    const expected = linkProof(this.secret, payload.body, payload.at)
    if (!b4a.equals(expected, payload.proof)) return
    if (Math.abs(Date.now() - payload.at) > 10 * 60 * 1000) return

    this._finish(payload.body)
  }
}

/**
 * Show a code, then wait to be handed an account.
 *
 * A thin reading of SealedBox: the body is the seed and the profile, and this
 * is what turns a device with no account into one that has this account.
 */
export class LinkOffer extends SealedBox {
  async start (opts) {
    const body = await super.start(opts)
    if (!body) return null
    try {
      const parsed = JSON.parse(body)
      if (typeof parsed.seed !== 'string' || parsed.seed.length !== 64) return null
      return { seed: parsed.seed, profile: parsed.profile || {} }
    } catch {
      return null
    }
  }
}

/**
 * The device that already holds the account: hand it over.
 *
 * Resolves once the payload has been written to a peer on the code's topic.
 * The caller is expected to have asked its owner first — see the note at the
 * top about the one attack this cannot see.
 */
export async function sendSealed (client, code, body, { timeout = 45000 } = {}) {
  const parsed = parseLinkCode(code)
  if (!parsed) throw new Error('That is not a Pine device code.')

  const topic = linkTopic(parsed.publicKey)
  const at = Date.now()
  const text = String(body)

  const payload = { body: text, at, proof: linkProof(parsed.secret, text, at) }

  const state = c.state()
  payloadEncoding.preencode(state, payload)
  state.buffer = b4a.allocUnsafe(state.end)
  payloadEncoding.encode(state, payload)
  const sealed = seal(state.buffer, toBoxPublicKey(parsed.publicKey))

  return new Promise((resolve, reject) => {
    const channels = new Set()
    const muxes = new Set()
    let done = false
    let retry = null

    const finish = (ok, err) => {
      if (done) return
      done = true
      clearTimeout(timer)
      if (retry) clearInterval(retry)
      stopListening()
      for (const mux of muxes) {
        try { mux.unpair({ protocol: PROTOCOL, id: topic }) } catch {}
      }
      for (const ch of channels) {
        try { ch.close() } catch {}
      }
      client.transport.leave(topic).catch(() => {})
      if (ok) resolve(true)
      else reject(err || new Error('Could not reach that device. Both need Pine open and online.'))
    }

    const timer = setTimeout(() => finish(false), timeout)

    // pair() before opening, for the reason inbox.js spells out: a channel
    // opened for a protocol the other side has not created yet is refused and
    // never retried.
    const attach = (stream) => {
      const mux = Protomux.from(stream)
      muxes.add(mux)
      mux.pair({ protocol: PROTOCOL, id: topic }, () => open(mux))
      open(mux)
    }

    const open = (mux) => {
      const channel = mux.createChannel({
        protocol: PROTOCOL,
        id: topic,
        onopen: () => {
          try {
            message.send(sealed)
            setTimeout(() => finish(true), 500)
          } catch (err) {
            finish(false, err)
          }
        },
        onclose: () => channels.delete(channel)
      })
      if (!channel) return
      const message = channel.addMessage({ encoding: frame })
      channel.open()
      channels.add(channel)
    }

    const stopListening = client.transport.onConnection((conn) => attach(conn))
    for (const conn of client.transport.connections) attach(conn)
    client.transport.join(topic, { server: false, client: true })

    retry = setInterval(() => {
      if (done) return
      client.transport.refresh(topic)
      for (const conn of client.transport.connections) attach(conn)
    }, 3000)
    if (retry.unref) retry.unref()
  })
}

/**
 * Hand this device's account to whoever is showing `code`.
 *
 * The dangerous direction: the receiver ends up holding the account. The
 * caller must have asked its owner first — see the note at the top about the
 * one attack no pairing scheme can see.
 */
export async function sendLink (client, code, opts) {
  const profile = client.identity.profile || {}
  return sendSealed(client, code, JSON.stringify({
    seed: b4a.toString(client.identity.seed, 'hex'),
    profile: {
      name: profile.name || '',
      bio: profile.bio || '',
      username: profile.username || '',
      avatar: profile.avatar || null
    }
  }), opts)
}
